import React, { useEffect, useState } from "react";
import {
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControl,
    FormControlLabel,
    InputLabel, MenuItem, OutlinedInput,
    Radio,
    RadioGroup, Select,
    Switch,
    useMediaQuery,
    useTheme
} from "@material-ui/core";

import Input from "@material-ui/core/Input";
import { observer } from "mobx-react";
import { useStores } from "../../../../../hooks";
import { locale } from "../../../../../common/consts";
import { changeCheckboxEventHandlerDecorator, changeEventHandlerDecorator, localize } from "../../../../../common/libs";
import {
    ExternalIntegrationType,
    IIkoModifierSetType,
    MenuItemModifierSetChooseManyData,
    MenuItemModifierSetChooseOneData,
    ModifiersSetType
} from "../../../../../services/menu/models";
import { useHistory } from "react-router-dom";
import { Space, TextField, Typography } from "../../../../../common/components/libs";
import chooseOneImg from "../assets/chooseOne.png"
import chooseManyImg from "../assets/chooseMany.png"
import makeStyles from "@material-ui/core/styles/makeStyles";
import { useTranslation } from "react-i18next";
import LocalizedInput from "../../../../../common/components/translation/LocalizedInput";
import { ModifiersFormType } from "../../../store/modifiersSets/forms";
import { LocalizedString } from "../../../../../services/general/models";


const useStyles = makeStyles({
    labelRoot: {
        alignItems: "flex-start",
        marginRight: 30
    },
    labelLabel: {
        minWidth: "100%"
    }
});

const EditModifiersSetForm = () => {
    const theme = useTheme();
    const isSm = useMediaQuery(theme.breakpoints.up('sm'));
    const { t } = useTranslation(["general", "menu"]);
    const [name, setName] = useState("");
    const [type, setType] = useState(ModifiersSetType.ChooseOne);
    const [integrationType, setIntegrationType] = useState(ExternalIntegrationType.iikoCloud);
    const [externalId, setExternalId] = useState<null | string>(null);
    const [modifiersSetExternalType, setModifiersSetExternalType] = useState<IIkoModifierSetType | null>(null);
    const [isCommonCompoundModifier, setIsCommonCompoundModifier] = useState<boolean>(false)
    const [localizedName, setLocalizedName] = useState<LocalizedString>();
    const [maxCountTotalChosen, setMaxCountTotalChosen] = useState(0);
    const [minCountTotalChosen, setMinCountTotalChosen] = useState(0);
    const [isRequired, setIsRequired] = useState(false);


    const { modifiersSetsStore } = useStores();
    const forms = modifiersSetsStore.forms;
    const { current, open, init, notifyOnDone } = forms.forms[ModifiersFormType.editMenuModifiersSet];
    const history = useHistory();

    const classes = useStyles();

    useEffect(() => {
        setName(current && localize(current.data.name) || init.name || "");
        setType(current?.data?.type || ModifiersSetType.ChooseOne);
        setLocalizedName(current?.data.name || { ru: init.name || "" });

        if (current?.data?.type === ModifiersSetType.ChooseMany) {
            setMaxCountTotalChosen(current.data.maxCountTotalChosen);
            setMinCountTotalChosen(current.data.minCountTotalChosen);
        } else if (current?.data?.type === ModifiersSetType.ChooseOne) {
            setIsRequired(current.data.isRequired);
        } else {
            setIsRequired(false);
            setMaxCountTotalChosen(0);
            setMinCountTotalChosen(0);
        }
        if(current){
            setExternalId(current.externalId)
            setModifiersSetExternalType(current.externalSettings?.type)
            setIsCommonCompoundModifier(current.externalSettings?.isCommonCompoundModifier || false)
        }else{
            setExternalId(null)
            setModifiersSetExternalType(null)
            setIsCommonCompoundModifier(false)
        }
    }, [current, init]);


    const closeForm = () => {
        forms.closeForm(ModifiersFormType.editMenuModifiersSet);
    };

    const handleSubmit = async () => {
        let data;
        if (type === ModifiersSetType.ChooseMany) {
            data = {
                name: {
                    [locale]: name
                }, modifiers: [],
                type,
                minCountTotalChosen,
                maxCountTotalChosen
            } as MenuItemModifierSetChooseManyData;
        } else if (type === ModifiersSetType.ChooseOne) {
            data = {
                name: { [locale]: name }, modifiers: [],
                type,
                isRequired
            } as MenuItemModifierSetChooseOneData;
        }

        if (current) {
            await modifiersSetsStore.updateModifiersSet({
                menuItemModifierSetId: current.id,
                data: {
                    ...data,
                    name: localizedName,
                    modifiers: current.data.modifiers,
                },
            });

            await modifiersSetsStore.updateModifiersSetExternalSettings({
                menuItemModifierSetId: current.id,
                externalId: externalId !== '' ? externalId : null,
                externalSettings: {
                    integrationType,
                    type: modifiersSetExternalType,
                    isCommonCompoundModifier
                }
            })
            closeForm();
        } else {
            const id = await modifiersSetsStore.createModifiersSet({
                data
            });
            if (notifyOnDone) {
                notifyOnDone(id);
            } else if (init.name && init.name.length === 0) {
                history.push(`/menus/modifiers/${id}/view`);
            }
            await modifiersSetsStore.updateModifiersSetExternalSettings({
                menuItemModifierSetId: id,
                externalId: externalId !== '' ? externalId : null,
                externalSettings: {
                    integrationType,
                    type: modifiersSetExternalType,
                    isCommonCompoundModifier
                }
            })
            closeForm();
        }

    };


    let title;
    if (current) {
        title = t("general:editing");
    } else {
        title = t("menu:modifiersSet.modifiersSetCreation");
    }

    return (
        <Dialog
            open={open}
            onClose={closeForm}
            aria-labelledby="form-dialog-title"
            fullWidth={true}
            maxWidth={"sm"}
            fullScreen={!isSm}
        >
            <DialogTitle id="form-dialog-title">
                {title}
            </DialogTitle>
            <DialogContent>
                <form autoComplete="off">
                    {
                        (current && current.data && current.data.name) &&
						<LocalizedInput
							label={t("general:name")}
							value={current.data.name}
							setNewValue={setLocalizedName}
						/>
                    }
                    {
                        !current &&
						<FormControl fullWidth margin={"normal"}>
							<InputLabel htmlFor="name">{t("general:name")}</InputLabel>
							<Input
								value={name}
								onChange={changeEventHandlerDecorator(setName)}
							/>
						</FormControl>
                    }


                    <FormControl fullWidth margin={"normal"}>
                        <Typography variant='h5' mb={3}
                                    mt={5}>{t("menu:modifiersSet.howManyModifiersCanYouChoose")}</Typography>
                        <RadioGroup value={type} onChange={changeEventHandlerDecorator(setType)}>
                            <FormControlLabel value={ModifiersSetType.ChooseOne}
                                              key={ModifiersSetType.ChooseOne}
                                              control={<Radio style={{ marginRight: 5 }}/>}
                                              classes={{
                                                  root: classes.labelRoot,
                                                  label: classes.labelLabel
                                              }}
                                              label={
                                                  <div style={
                                                      {
                                                          display: "flex",
                                                          alignItems: "center",
                                                          justifyContent: "space-between",
                                                          flexDirection: 'row',
                                                      }
                                                  }>
                                                      <div>
                                                          <span
                                                              style={{ fontWeight: 900 }}>
                                                              {t(`menu:modifiersSetType.ChooseOne`)}
                                                          </span>
                                                          <br/>
                                                          <span>
                                                                {t("menu:modifiersSet.chooseOneVariant")}
                                                          </span>
                                                      </div>
                                                      <div>
                                                          <img src={chooseOneImg} style={{ maxHeight: 40 }}/>
                                                      </div>
                                                  </div>
                                              }/>

                            <Space size={5}/>

                            <FormControlLabel value={ModifiersSetType.ChooseMany}
                                              key={ModifiersSetType.ChooseMany}
                                              control={<Radio style={{ marginRight: 5 }}/>}
                                              classes={{
                                                  root: classes.labelRoot,
                                                  label: classes.labelLabel
                                              }}
                                              label={
                                                  <div style={
                                                      {
                                                          display: "flex",
                                                          alignItems: "center",
                                                          justifyContent: "space-between",
                                                          flexDirection: 'row',
                                                          minWidth: "100%"

                                                      }
                                                  }>
                                                      <div>
                                                          <span
                                                              style={{ fontWeight: 900 }}>
                                                              {t(`menu:modifiersSetType.ChooseMany`)}
                                                          </span>
                                                          <br/>
                                                          <span>
                                                              {t("menu:modifiersSet.anyCountInRange")}
                                                          </span>
                                                      </div>
                                                      <div>
                                                          <img src={chooseManyImg} style={{ maxHeight: 40 }}/>
                                                      </div>
                                                  </div>
                                              }/>
                        </RadioGroup>
                    </FormControl>

                    <Typography variant='h5' mt={10}>{t("general:settings")}</Typography>

                    {type === ModifiersSetType.ChooseMany && <>
						<FormControl fullWidth margin={"normal"}>
							<InputLabel htmlFor="name">{t("menu:modifiersSet.chooseNoLess")}</InputLabel>
							<Input
								value={minCountTotalChosen}
								onChange={changeEventHandlerDecorator(setMinCountTotalChosen)}
							/>
						</FormControl>
						<FormControl fullWidth margin={"normal"}>
							<InputLabel htmlFor="name">{t("menu:modifiersSet.chooseNoMore")}</InputLabel>
							<Input
								value={maxCountTotalChosen}
								onChange={changeEventHandlerDecorator(setMaxCountTotalChosen)}
							/>
						</FormControl>
					</>
                    }

                    {
                        type === ModifiersSetType.ChooseOne && <>
							<FormControl>
								<FormControlLabel
									control={
                                        <Switch
                                            checked={isRequired}
                                            onChange={changeCheckboxEventHandlerDecorator(setIsRequired)}
                                            value={isRequired}
                                        />
                                    }
									label={t("menu:modifiersSet.makeChoiceOfModifierSetRequired")}
								/>
							</FormControl>
						</>
                    }

                    <Typography variant='h5' mt={10} mb={5}>{t("menu:modifiersSet.integrations")}</Typography>
                    {
                        integrationType === ExternalIntegrationType.iikoCloud
                        && <>

							<FormControl variant="outlined" fullWidth={true}>
								<InputLabel>
                                    {t("menu:modifiersSet.externalIIkoSetType")}
								</InputLabel>
								<Select
									value={modifiersSetExternalType}
									onChange={changeEventHandlerDecorator(setModifiersSetExternalType)}
									input={<OutlinedInput labelWidth={173}/>}
								>
									<MenuItem value={null} disabled>
										<MenuItem value="default">{t("menu:menuEditor.notSelected")}</MenuItem>
									</MenuItem>
									<MenuItem value={IIkoModifierSetType.priceSize}>
                                        {t("menu:modifiersSet.iikoModifiersSetTypes.priceSizes")}
									</MenuItem>
									<MenuItem value={IIkoModifierSetType.modifiers}>
                                        {t("menu:modifiersSet.iikoModifiersSetTypes.modifiers")}
									</MenuItem>
									<MenuItem value={IIkoModifierSetType.groupModifiers}>
                                        {t("menu:modifiersSet.iikoModifiersSetTypes.groupModifiers")}
									</MenuItem>
								</Select>
							</FormControl>

                            <FormControl>
                                <FormControlLabel
                                    control={
                                        <Switch
                                            checked={isCommonCompoundModifier}
                                            onChange={changeCheckboxEventHandlerDecorator(setIsCommonCompoundModifier)}
                                            value={isCommonCompoundModifier}
                                        />
                                    }
                                    label={"Общий набор (только для сборных позиций iiko)"}
                                />
                            </FormControl>

							<FormControl fullWidth margin={"normal"}>
								<TextField
									label={t("menu:modifiersSet.externalId")}
									fullWidth={true}
									value={externalId || ""}
									onChange={changeEventHandlerDecorator(setExternalId)}
								>
								</TextField>
							</FormControl>
						</>
                    }
                </form>
            </DialogContent>

            <DialogActions>
                <Button onClick={closeForm} color="primary">
                    {t("general:cancel")}
                </Button>
                <Button
                    color="primary"
                    variant='contained'
                    onClick={handleSubmit}
                >
                    {t("general:continue")}
                </Button>
            </DialogActions>
        </Dialog>
    );

};

export default observer(EditModifiersSetForm);
